import React, { forwardRef } from 'react'
import { cn } from 'utils/analytics/cn'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  error?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant = 'default',
      size = 'md',
      error,
      leftIcon,
      rightIcon,
      leftElement,
      rightElement,
      disabled,
      ...props
    },
    ref
  ) => {
    // Prevent mouse wheel from changing number input values
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur()
    }
    const baseStyles = 'w-full transition-colors duration-200 focus:outline-none'

    const variantStyles = {
      default: 'bg-kodiak-card-secondary border border-kodiak-border-primary focus:border-kodiak-accent',
      outline: 'bg-transparent border border-kodiak-border-primary focus:border-kodiak-accent',
      ghost: 'bg-transparent border-0 focus:bg-kodiak-card-secondary',
    }

    const sizeStyles = {
      sm: 'px-3 py-2 text-sm rounded-md',
      md: 'px-4 py-3 text-base rounded-lg',
      lg: 'px-6 py-4 text-lg rounded-xl',
    }

    const errorStyles = error ? 'border-kodiak-accent-error focus:border-kodiak-accent-error' : ''

    const disabledStyles = disabled
      ? 'opacity-50 cursor-not-allowed bg-kodiak-card-tertiary'
      : 'hover:border-kodiak-border-secondary'

    const textStyles = 'text-kodiak-text-primary placeholder:text-kodiak-text-muted'

    const inputClasses = cn(
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      errorStyles,
      disabledStyles,
      textStyles,
      leftIcon || leftElement ? 'pl-10' : '',
      rightIcon || rightElement ? 'pr-10' : '',
      // Hide spinner arrows for number inputs
      props.type === 'number'
        ? '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
        : '',
      className
    )

    return (
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-kodiak-text-muted">{leftIcon}</div>
        )}
        {leftElement && <div className="absolute left-3 top-1/2 transform -translate-y-1/2">{leftElement}</div>}

        <input ref={ref} disabled={disabled} className={inputClasses} onWheel={handleWheel} {...props} />

        {rightIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-kodiak-text-muted">{rightIcon}</div>
        )}
        {rightElement && <div className="absolute right-3 top-1/2 transform -translate-y-1/2">{rightElement}</div>}
      </div>
    )
  }
)

Input.displayName = 'Input'

export { Input }
